'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import WorkspaceSelection from '../signin-new/components/workspace-selection';
import ClientSelection from '../signin-new/components/client-selection';
import ProductSelection from '../signin-new/components/product-selection';
import { useDispatch, useSelector } from 'react-redux';
import { setData } from '../../store/Home/authSlice';
import { get } from 'lodash';
import { authService, updateUserWorkspace, getRedirectPath, getCookie, getClients, setWorkspace, getSvcCategoryProducts } from '../../services/api';
import { buildFinalRedirect } from '../../utils/redirect';
import { PARAMS } from '../../params';

const WorkspaceSelectionPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    // Auth slice shape (from authSlice.ts:28-32):
    //   state.auth.userData      = action.payload.data        (no accesstoken inside)
    //   state.auth.accessToken   = action.payload.accesstoken (camelCase, top-level)
    //   state.auth.refreshToken  = action.payload.refreshtoken
    // Earlier fix attempts read `userData?.accesstoken` which is ALWAYS undefined
    // → fallback to '' → token wiped. Must read `accessToken` / `refreshToken`
    // top-level from the auth slice.
    const { userData, loading, accessToken, refreshToken } = useSelector((state: any) => state.auth);
    const showBack = (location.state as any)?.showBack === true;
    const userInfo = userData?.userinfo;
    const [selectedWorkspace, setSelectedWorkspace] = useState<any>(null);
    const [selectedProduct, setSelectedProduct] = useState<any>(null);
    const [showClientSelection, setShowClientSelection] = useState(false);
    const [showProductSelection, setShowProductSelection] = useState(false);
    // Tracks whether the single-workspace auto-select flow has resolved into a picker.
    // Unlike `staffingDetails.length === 1`, this doesn't flip back once the user backs
    // out of the product/client picker — see the spinner guard near the bottom.
    const [autoSelectResolved, setAutoSelectResolved] = useState(false);

    // Auto-completion guard. The bootstrap useEffect below depends on `userInfo`,
    // but Redux dispatches return new object references on every update —
    // including the response from updateUserWorkspace mid-flow. Without this
    // ref, the effect re-fires and auto-completes corp/workspace selection
    // again, producing duplicate set-workspace + clients calls in the network
    // tab (4–5 of each instead of 1). The ref locks the auto-fire path to
    // exactly one execution per mount.
    const hasAutoCompletedRef = useRef(false);

    // Build the terminal redirect URL out of the auth FE.
    //
    // - workspace_data is non-credential routing info; safe on any origin.
    // - Token is appended ONLY for localhost targets (security gate, via
    //   buildFinalRedirect). For deployed targets, auth rides the HttpOnly
    //   cookie on .veytan.com — token never appears in URL.
    //
    // Token source: top-level `accessToken` from the auth slice (populated by
    //   signin response in api.ts dispatches → authSlice.ts:29). Auth slice
    //   shape puts the token at state.auth.accessToken (camelCase), NOT at
    //   state.auth.userData.accesstoken — that path doesn't exist.
    //   DO NOT use getCookie(PARAMS.TOKEN_STORAGE_KEY) — that returns null for
    //   the HttpOnly cookie on deployed, so localhost FE bootstrap would
    //   receive a token of literally "null".
    const getRedirectUrl = (workspaceData: any = null, clientData: any = null) => {
        const queryParams = new URLSearchParams(location.search);
        const baseRedirect = queryParams.get('redirect') || PARAMS.DEFAULT_REDIRECT_URL;

        const workspaceParams = workspaceData
            ? {
                workspace: workspaceData.workspace || {},
                client: workspaceData.client || {},
                corporation_id: workspaceData.corporation_id || '',
                parent_corp_id: workspaceData.parent_corp_id || '',
                parent_legal_entity_id: workspaceData.parent_legal_entity_id || '',
                parent_legal_entity_name: workspaceData.parent_legal_entity_name || '',
                legal_entity_id: workspaceData.legal_entity_id || '',
                svc_cat_id: workspaceData.svc_cat_id || ''
            }
            : undefined;

        const token = accessToken || undefined;

        return buildFinalRedirect(baseRedirect, {
            token,
            workspaceData: workspaceParams,
        });
    };

    const handleAuthRedirect = () => {
        navigate('/signin-new');
    };

    const handleBackToCorpSelection = () => {
        const params = new URLSearchParams(location.search);
        const redirectParam = params.get('redirect');
        const corpPath = redirectParam ? `/corp-selection?redirect=${encodeURIComponent(redirectParam)}` : '/corp-selection';
        navigate(corpPath);
    };

    console.log('Redux State:', { userData, loading, userInfo });

    useEffect(() => {
        // If no user info in Redux, try to get from storage
        if (!userInfo) {
            try {
                const storedUserInfo = authService.getUser();
                if (storedUserInfo) {
                    console.log('🔍 Stored User Info:', storedUserInfo);
                    dispatch(setData({
                        data: { userinfo: storedUserInfo },
                        // authService.getToken returns null for HttpOnly cookies — fall back to
                        // whatever's currently in Redux so a re-mount from storage doesn't wipe
                        // an existing access token. Read top-level `accessToken` from auth slice
                        // (NOT userData.accesstoken — that path doesn't exist in this shape).
                        accesstoken: authService.getToken() || accessToken || '',
                        refreshtoken: refreshToken || ''
                    }));
                } else {
                    handleAuthRedirect();
                }
            } catch (error) {
                console.error('Error getting user info:', error);
                handleAuthRedirect();
            }
            return;
        }

        // Single-fire guard: auto-completion of corp/workspace selection must
        // run exactly once per mount. Without this, Redux updates from
        // updateUserWorkspace / setWorkspace mid-flow change the `userInfo`
        // object reference, re-firing this effect and producing duplicate
        // set-workspace + clients calls.
        if (hasAutoCompletedRef.current) return;

        const staffingDetails = userInfo.staffing_corp_access_list?.staffing_details || [];
        const hasParentCorp = !!userInfo.parentCorpData;
        // Backend signal that the user must be shown an explicit choice even when a
        // given category (corp-only / staffing-only) currently resolves to a single
        // option — e.g. parentCorpData + exactly one staffing entry both present.
        // Auto-selecting through either branch below would silently pick one for them.
        const isMultiSelection = !!userInfo.multiSelection;

        if (staffingDetails.length === 0 && !hasParentCorp) {
            const redirectPath = getRedirectPath(userInfo);
            navigate(redirectPath);
        } else if (staffingDetails.length === 0 && hasParentCorp && !isMultiSelection) {
            // Only parentCorp, no staffing list → auto-select corp and redirect directly
            hasAutoCompletedRef.current = true;
            handleCorporationSelect({ ...userInfo.parentCorpData, type: 'corporation' });
        } else if (staffingDetails.length === 1 && !isMultiSelection) {
            // Single workspace → auto-select and skip the selection screen
            hasAutoCompletedRef.current = true;
            handleWorkspaceSelect(staffingDetails[0]);
        }
        // Note: when neither auto-completion branch fires (including whenever
        // isMultiSelection is true), user picks a workspace manually via the UI —
        // handleWorkspaceSelect runs once from the click handler, so no guard needed there.
    }, [userInfo, navigate, dispatch]);

    const handleWorkspaceSelect = async (workspaceData: any) => {
        setSelectedWorkspace(workspaceData);

        if (workspaceData.type === 'corporation') {
            handleCorporationSelect(workspaceData);
        } else {
            const svcCatCode = workspaceData.svc_cat_details?.code;
            if (svcCatCode) {
                const productsResponse = await getSvcCategoryProducts(svcCatCode);
                const products = Array.isArray(productsResponse?.data) ? productsResponse.data : [];
                if (products.length === 1) {
                    // Single product → auto-select and skip the picker, same as the
                    // single-workspace and single-client auto-select paths.
                    handleProductSelect(products[0]);
                    return;
                } else if (products.length > 1) {
                    setAutoSelectResolved(true);
                    setShowProductSelection(true);
                    return;
                }
            }
            const response = await getClients(workspaceData.svc_cat_id.toString(), userInfo.id.toString(), userInfo?.parent_corp_id);
            const clients = Array.isArray(response?.data) ? response.data : [];
            if (clients.length === 1) {
                handleClientSelect(clients[0], workspaceData);
            } else {
                setAutoSelectResolved(true);
                setShowClientSelection(true);
            }
        }
    };

    const handleProductSelect = async (productData: any) => {
        setSelectedProduct(productData);
        setShowProductSelection(false);
        const response = await getClients(productData.svc_cat_id.toString(), userInfo.id.toString(), userInfo?.parent_corp_id);
        const clients = Array.isArray(response?.data) ? response.data : [];
        if (clients.length === 1) {
            handleClientSelect(clients[0], selectedWorkspace);
        } else {
            setAutoSelectResolved(true);
            setShowClientSelection(true);
        }
    };

    const handleBackFromProducts = () => {
        setShowProductSelection(false);
        setSelectedProduct(null);
        setSelectedWorkspace(null);
    };

    // Function to handle navigation after successful workspace selection
    const navigateAfterSelection = (userInfo: any) => {
        // Get the redirect URL (either from query params or default)
        const redirectUrl = getRedirectUrl();
        
        // Navigate based on the URL type
        if (redirectUrl.startsWith('/')) {
            // Internal redirect
            navigate(redirectUrl);
        } else {
            // External redirect - use window.location
            window.location.href = redirectUrl;
        }
    };

    const handleCorporationSelect = async (workspaceData: any) => {
        try {
            // Auth is enforced by HttpOnly cookies on .veytan.com; nothing for JS to check pre-flight.
            // If the session is invalid, updateUserWorkspace below fails with 401 → handled at line ~142.
            const result = await updateUserWorkspace(userInfo, workspaceData);
            
            if (result.error) {
                console.error('Error updating workspace:', result.message);
                handleAuthRedirect();
                return;
            }
            
            // Create workspace data object with required properties
            const wsData = {
                workspace: {
                    corporation_ids: workspaceData.corporation_ids || [],
                    legal_entity_ids: workspaceData.legal_entity_ids || [],
                    svc_cat_id: workspaceData.svc_cat_id,
                    svc_cat_details: workspaceData.svc_cat_details || {},
                    type: workspaceData.type
                },
                client: {
                    clt_corporation_details: {
                        id: workspaceData.id,
                        full_name: workspaceData.full_name,
                        logo_url: Array.isArray(workspaceData.logo_url)
                            ? workspaceData.logo_url[0]?.presigned_url || null
                            : workspaceData.logo_url || null
                    }
                },
                corporation_id: workspaceData.corporation_id || workspaceData?.id,
                parent_corp_id: workspaceData.id,
                parent_legal_entity_id: userInfo?.parent_legal_entity_id || null,
                parent_legal_entity_name: userInfo?.parent_legal_entity_name || null,
                legal_entity_id: workspaceData.legal_entity_id,
                svc_cat_id: workspaceData.svc_cat_id
            };

            // Update Redux store.
            // result.token comes from updateUserWorkspace, which typically doesn't return a
            // token (it just confirms the workspace selection). Without the userData
            // fallback, accesstoken would get wiped to '' here — and workspace-selection's
            // getRedirectUrl below reads userData.accesstoken to build the ?token= URL
            // append for localhost redirects. That's how the localhost handoff would break.
            dispatch(setData({
                data: { userinfo: result.userInfo },
                accesstoken: result.token || accessToken || '',
                refreshtoken: result.refreshToken || refreshToken || ''
            }));

            // Persist workspace context to BE so /decodeToken on hrms-01 can merge it into userinfo.
            // Failure here is non-fatal — URL workspace_data still seeds Redux, and Stage 2
            // will treat the cookie as authoritative — but log it for diagnosis.
            const setRes = await setWorkspace(wsData);
            if (setRes?.error) {
                console.warn('setWorkspace failed (continuing anyway):', setRes.message);
            }

            // Redirect with workspace data in URL
            const redirectUrl = getRedirectUrl(wsData);
            if (redirectUrl.startsWith('/')) {
                navigate(redirectUrl);
            } else {
                window.location.href = redirectUrl;
            }
        } catch (error) {
            console.error('Error handling corporation selection:', error);
            handleAuthRedirect();
        }
    };

    const handleClientSelect = async (clientData: any, workspaceOverride?: any) => {
        try {
            // Auth is enforced by HttpOnly cookies on .veytan.com; nothing for JS to check pre-flight.
            // If the session is invalid, updateUserWorkspace below fails with 401 → handled at result.error check.
            const workspace = workspaceOverride ?? selectedWorkspace;
            const result = await updateUserWorkspace(userInfo, workspace, true, clientData);
            console.log('Result:', result);
            if (result.error) {
                console.error('Error updating client selection:', result.message);
                handleAuthRedirect();
                return;
            }

            // Create workspace data object with required properties
            const wsData = {
                workspace: {
                    corporation_ids: workspace.corporation_ids || [],
                    legal_entity_ids: workspace.legal_entity_ids || [],
                    svc_cat_id: workspace.svc_cat_id,
                    svc_cat_details: workspace.svc_cat_details || {},
                    type: workspace.type
                },
                client: {
                    svc_cat_id: clientData.svc_cat_id,
                    clt_corporation_details: clientData.clt_corporation_details || {},
                    svc_cat_details: clientData.svc_cat_details || {},
                    // clt_legal_entity_details: clientData?.clt_legal_entity_details || {}
                },
                corporation_id: clientData.clt_corporation_details?.id,
                parent_corp_id: userInfo?.parent_corp_id || null,
                parent_legal_entity_id: userInfo?.parent_legal_entity_id || null,
                parent_legal_entity_name: userInfo?.parent_legal_entity_name || null,
                legal_entity_id: clientData.clt_legal_entity_details?.id,
                svc_cat_id: clientData.svc_cat_id
            };
            
            // Update Redux store.
            // Same pattern as handleCorporationSelect above — preserve the existing
            // Redux accesstoken if updateUserWorkspace's response doesn't include a fresh
            // one. Otherwise getRedirectUrl below skips the ?token= append for localhost.
            dispatch(setData({
                data: { userinfo: result.userInfo },
                accesstoken: result.token || accessToken || '',
                refreshtoken: result.refreshToken || refreshToken || ''
            }));

            // Persist workspace context to BE so /decodeToken on hrms-01 can merge it into userinfo.
            // Failure here is non-fatal — URL workspace_data still seeds Redux — but log it.
            const setRes = await setWorkspace(wsData);
            if (setRes?.error) {
                console.warn('setWorkspace failed (continuing anyway):', setRes.message);
            }

            // Redirect with workspace data in URL
            const redirectUrl = getRedirectUrl(wsData);
            if (redirectUrl.startsWith('/')) {
                navigate(redirectUrl);
            } else {
                window.location.href = redirectUrl;
            }
        } catch (error) {
            console.error('Error handling client selection:', error);
            handleAuthRedirect();
        }
    };

    const handleBackToWorkspaces = () => {
        setShowClientSelection(false);
        setSelectedWorkspace(null);
        setSelectedProduct(null);
    };

    // Show spinner when loading, no userInfo, single workspace (auto-selecting), or no staffing details.
    // `isAutoSelecting` is gated on `autoSelectResolved` (explicit state) rather than
    // `staffingDetails.length === 1` (static data) — the latter would flip back to true,
    // and re-trigger this spinner forever, the moment the user backs out of the resulting
    // product/client picker, since staffingDetails never changes across re-renders.
    // `isMultiSelection` mirrors the bootstrap effect above: when the backend says the
    // user must choose explicitly, neither the single-staffing nor the corp-only path
    // auto-selects, so the spinner must not gate on those cases either.
    const staffingDetails = userInfo?.staffing_corp_access_list?.staffing_details || [];
    const hasParentCorp = !!userInfo?.parentCorpData;
    const hasStaffingDetails = staffingDetails.length > 0;
    const isMultiSelection = !!userInfo?.multiSelection;
    const isAutoSelecting =
        (staffingDetails.length === 1 && !isMultiSelection && !autoSelectResolved) ||
        (staffingDetails.length === 0 && hasParentCorp && !isMultiSelection);
    if (loading || !userInfo || (!hasStaffingDetails && !hasParentCorp) || isAutoSelecting) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-[#F9FAFB]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F9FAFB] relative">
            {/* Background Pattern */}
            <div 
                className="absolute inset-0 pointer-events-none opacity-50"
                style={{
                    backgroundImage: 'url(/assets/login/workspace-pattern.svg)',
                    backgroundRepeat: 'repeat'
                }}
            />
            
            {/* Logo */}
            <div className="absolute top-0 left-0 w-full text-2xl text-[#6941C6] font-[Montserrat Alternates] font-bold leading-normal pt-6 pl-9 tracking-[-1.12px]">
                Veytan
            </div>

            {/* Content */}
            <div className="flex justify-center items-center min-h-screen">
                <div className="w-full max-w-[480px] px-4 relative z-10">
                    {showClientSelection ? (
                        <ClientSelection
                            serviceName={selectedWorkspace?.svc_cat_details?.display || ''}
                            onBack={handleBackToWorkspaces}
                            onClientSelect={handleClientSelect}
                            svcCatId={selectedWorkspace?.svc_cat_id}
                            userId={userInfo.id}
                            parentCorpId={userInfo?.parent_corp_id}
                        />
                    ) : showProductSelection ? (
                        <ProductSelection
                            svcCatCode={selectedWorkspace?.svc_cat_details?.code || ''}
                            workspaceName={selectedWorkspace?.svc_cat_details?.display || ''}
                            onBack={handleBackFromProducts}
                            onProductSelect={handleProductSelect}
                        />
                    ) : (
                        <WorkspaceSelection
                            staffingCorpAccessList={userInfo.staffing_corp_access_list}
                            onWorkspaceSelect={handleWorkspaceSelect}
                            onBack={showBack ? handleBackToCorpSelection : undefined}
                        />
                    )}
                </div>
            </div>

            {/* Footer */}
            {/* <div className="absolute bottom-0 w-full flex justify-center pb-8">
                <img src="/assets/login/buzzworks_logo.svg" alt="Buzzworks Logo" />
            </div> */}
        </div>
    );
};

export default WorkspaceSelectionPage; 